import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { COLORS } from "constant/adminTheme";

/**
 * 共用 tooltip
 *
 * # 為什麼不用原生 `title`
 *
 * 原生 title 有四個沒得改的問題：**延遲約 1 秒才出現**、樣式完全不能動、
 * 長文字的換行由瀏覽器決定（常常變成很難讀的一長條）、**觸控裝置上不會出現**。
 * 後臺有不少「不看說明就會誤讀」的數字（歸集手續費的認列週、JP 排除、
 * 費用不明的筆數…），那些說明值得被看見。
 *
 * # 為什麼用 portal + fixed
 *
 * 這些觸發點很多在表格裡，而表格常常有 `overflow-x: auto`。
 * 用 `::after` 之類的純 CSS tooltip 會被那個 overflow **裁掉**，
 * 而且愈是需要說明的欄位（通常在右邊）愈容易被裁。
 * 所以改成 render 到 `document.body` 並用 `position: fixed` 定位 —— 不會被任何
 * 祖先裁切，也不受 z-index 堆疊影響。
 *
 * # 游標
 *
 * 觸發點一律 `cursor: help`。這是坊間對「純資訊、點了不會怎樣」的慣例
 *（Bootstrap / Ant Design / `<abbr>` 都是），跟 `pointer`（會發生事情）
 * 明確區分開來 —— 游標本身就是一種預告。
 *
 * # 可及性
 *
 * hover 之外也吃 focus，所以鍵盤 tab 得到；Esc 關閉。
 * 觸發點帶 `tabIndex=0` 與 `aria-describedby`。
 */

const GAP = 10;          // tooltip 與觸發點的距離
const MAX_W = 320;

function TinyTooltip({ content, children, placement = "top", maxWidth = MAX_W }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0, flipped: false });
  const triggerRef = React.useRef(null);
  const bubbleRef = React.useRef(null);
  const id = React.useRef(`tt-${Math.random().toString(36).slice(2, 9)}`).current;

  const place = React.useCallback(() => {
    const t = triggerRef.current;
    const b = bubbleRef.current;
    if (!t || !b) return;
    const r = t.getBoundingClientRect();
    const bw = b.offsetWidth;
    const bh = b.offsetHeight;

    // 上方放不下就翻到下方 —— 表格最上面那幾列一定會遇到
    let flipped = placement === "bottom";
    if (placement === "top" && r.top - bh - GAP < 8) flipped = true;
    if (placement === "bottom" && r.bottom + bh + GAP > window.innerHeight - 8) flipped = false;

    let left = r.left + r.width / 2 - bw / 2;
    // 貼邊時往內收，不要跑出畫面
    left = Math.max(8, Math.min(left, window.innerWidth - bw - 8));
    const top = flipped ? r.bottom + GAP : r.top - bh - GAP;
    setPos({ top, left, flipped });
  }, [placement]);

  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    // 捲動 / 改變視窗大小時直接關掉，比追著重新定位單純也不會抖
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!content) return children;

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={0}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{ display: "inline-flex", alignItems: "center", cursor: "help", outline: "none" }}
      >
        {children}
      </span>
      {open &&
        ReactDOM.createPortal(
          <div
            id={id}
            role="tooltip"
            ref={bubbleRef}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              zIndex: 2000,
              maxWidth,
              padding: "9px 12px",
              borderRadius: 8,
              // 比卡片亮一階 + 邊框 + 陰影，才不會跟底下的卡片糊在一起
              background: COLORS.headerBg,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
              color: COLORS.textSecondary,
              fontSize: 12.5,
              lineHeight: 1.65,
              fontWeight: 400,
              textAlign: "left",
              whiteSpace: "normal",
              pointerEvents: "none",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}

TinyTooltip.propTypes = {
  /** 說明內容。可以是字串，也可以是帶 <strong>/<br> 的節點 */
  content: PropTypes.node,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(["top", "bottom"]),
  maxWidth: PropTypes.number,
};

export default TinyTooltip;
